(() => {
  const DEFAULT_NOTE = 'You are the sweetest words written on my heart.\nI love you so much.';
  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);

  const decodeValue = (value) => {
    if (typeof value !== 'string') return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const normalizeProperties = (properties) => {
    if (!properties) return {};
    const normalizeEntry = (result, key, value) => {
      result[decodeValue(key)] = decodeValue(value);
      return result;
    };

    if (!Array.isArray(properties) && typeof properties === 'object') {
      return Object.entries(properties).reduce((result, [key, value]) => {
        if (Array.isArray(value) && value.length >= 2) return normalizeEntry(result, value[0], value[1]);
        if (value && typeof value === 'object' && value.first) return normalizeEntry(result, value.first, value.last);
        return normalizeEntry(result, key, value);
      }, {});
    }

    if (Array.isArray(properties)) {
      return properties.reduce((result, property) => {
        if (Array.isArray(property) && property.length >= 2) normalizeEntry(result, property[0], property[1]);
        if (property && typeof property === 'object' && property.first) normalizeEntry(result, property.first, property.last);
        return result;
      }, {});
    }

    return {};
  };

  const getFileName = (value) => {
    const cleanValue = String(value || '').split('?')[0];
    return cleanValue.split('/').pop() || 'Your current photo will be kept.';
  };

  const initHeartPhotoPersonalizer = (root) => {
    if (root.dataset.renaoHeartPhotoReady === 'true') return;
    root.dataset.renaoHeartPhotoReady = 'true';

    const form = root.closest('form');
    const preview = root.querySelector('.renao-heart-photo-personalizer__preview');
    const card = root.querySelector('[data-renao-heart-photo-card]');
    const bulkInput = root.querySelector('[data-renao-heart-bulk-input]');
    const bulkButton = root.querySelector('[data-renao-heart-bulk-button]');
    const photoInput = root.querySelector('[data-renao-heart-photo-input]');
    const photoPreview = root.querySelector('[data-renao-heart-photo-preview]');
    const photoButton = root.querySelector('[data-renao-heart-photo-button]');
    const photoAdjust = root.querySelector('[data-renao-heart-photo-adjust]');
    const photoPosition = root.querySelector('[data-renao-heart-photo-position]');
    const photoFileName = root.querySelector('[data-renao-heart-photo-file-name]');
    const progressCount = root.querySelector('[data-renao-heart-photo-count]');
    const toneProperty = root.querySelector('[data-renao-heart-tone-property]');
    const toneButtons = root.querySelectorAll('[data-renao-heart-tone-option]');
    const noteInput = root.querySelector('[data-renao-heart-note-input]');
    const notePreview = root.querySelector('[data-renao-heart-note-preview]');
    const noteCount = root.querySelector('[data-renao-heart-note-count]');
    const noteStyleProperty = root.querySelector('[data-renao-heart-note-style-property]');
    const noteStyleButtons = root.querySelectorAll('button[data-renao-heart-note-style]');
    const proofOpen = root.querySelector('[data-renao-heart-proof-open]');
    const proofModal = root.querySelector('[data-renao-heart-proof-modal]');
    const proofStage = root.querySelector('[data-renao-heart-proof-stage]');
    const proofCloseButtons = root.querySelectorAll('[data-renao-heart-proof-close]');
    const cropModal = root.querySelector('[data-renao-heart-crop]');
    const cropStage = root.querySelector('[data-renao-heart-crop-stage]');
    const cropImage = root.querySelector('[data-renao-heart-crop-image]');
    const cropZoom = root.querySelector('[data-renao-heart-crop-zoom]');
    const cropApply = root.querySelector('[data-renao-heart-crop-apply]');
    const cropCancelButtons = root.querySelectorAll('[data-renao-heart-crop-cancel]');
    const cropMoveButtons = root.querySelectorAll('[data-renao-heart-crop-move]');
    const cropZoomStepButtons = root.querySelectorAll('[data-renao-heart-crop-zoom-step]');
    const cropReset = root.querySelector('[data-renao-heart-crop-reset]');
    const previewOriginalParent = preview?.parentElement || null;
    const previewOriginalNext = preview?.nextSibling || null;
    let objectUrl = '';
    let existingPhotoUrl = '';
    let selectedPhotoFile = null;
    let cropState = { scale: 1, x: 0, y: 0 };
    let dragState = null;

    if (!form || !preview || !card || !photoInput || !photoPreview || !noteInput || !notePreview) return;
    root.dataset.renaoFileManaged = 'true';
    if (proofModal && proofModal.parentElement !== document.body) document.body.appendChild(proofModal);
    if (cropModal && cropModal.parentElement !== document.body) document.body.appendChild(cropModal);

    const getProductMediaWrapper = () => {
      const product = root.closest('.product') || document.querySelector('.product');
      return product ? product.querySelector('.product__media-wrapper') : null;
    };

    const hasPhoto = () => Boolean(objectUrl || existingPhotoUrl || (photoInput.files && photoInput.files.length));
    const getPhotoUrl = () => objectUrl || existingPhotoUrl || photoPreview.currentSrc || photoPreview.src || '';
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const updatePhotoPosition = () => {
      if (photoPosition) {
        const normalized = Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)
          ? `, nx:${cropState.normalizedX.toFixed(6)}, ny:${cropState.normalizedY.toFixed(6)}`
          : '';
        const legacy = `x:${Math.round(cropState.x)}, y:${Math.round(cropState.y)}, zoom:${cropState.scale.toFixed(2)}${normalized}`;
        photoPosition.value = globalThis.RenaoCropRecipe?.merge
          ? globalThis.RenaoCropRecipe.merge(photoPosition.value, legacy, photoPreview, photoPreview?.parentElement)
          : legacy;
      }
    };

    const clampCrop = () => {
      const visibleCropStage = cropStage && !cropModal?.hidden && cropStage.clientWidth && cropStage.clientHeight
        ? cropStage
        : null;
      const previewStage = photoPreview.parentElement;
      const stage = visibleCropStage || (previewStage?.clientWidth && previewStage?.clientHeight ? previewStage : null);
      const width = stage?.clientWidth || 0;
      const height = stage?.clientHeight || 0;
      if (!width || !height) return;
      if (!cropState.normalizedPositionApplied
        && Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY)) {
        cropState.x = cropState.normalizedX * width;
        cropState.y = cropState.normalizedY * height;
        cropState.normalizedPositionApplied = true;
      }
      const maxX = Math.max(0, (width * (cropState.scale - 1)) / 2);
      const maxY = Math.max(0, (height * (cropState.scale - 1)) / 2);
      cropState.x = clamp(cropState.x, -maxX, maxX);
      cropState.y = clamp(cropState.y, -maxY, maxY);
      cropState.normalizedX = cropState.x / width;
      cropState.normalizedY = cropState.y / height;
    };

    const applyPhotoTransform = () => {
      clampCrop();
      const previewFrame = photoPreview.parentElement;
      const previewWidth = previewFrame?.clientWidth || previewFrame?.getBoundingClientRect?.().width || 0;
      const previewHeight = previewFrame?.clientHeight || previewFrame?.getBoundingClientRect?.().height || 0;
      const hasNormalizedPosition =
        Number.isFinite(cropState.normalizedX) && Number.isFinite(cropState.normalizedY);
      const previewX = hasNormalizedPosition && previewWidth ? cropState.normalizedX * previewWidth : cropState.x;
      const previewY = hasNormalizedPosition && previewHeight ? cropState.normalizedY * previewHeight : cropState.y;

      // The crop dialog and the product preview have different pixel sizes. Keep the
      // dialog in its own coordinate space, but always render the product preview from
      // the normalized recipe so the exact crop saved for PDF is viewport-independent.
      photoPreview.style.transform = `translate(${previewX}px, ${previewY}px) scale(${cropState.scale})`;
      if (cropImage) {
        cropImage.style.transform = `translate(${cropState.x}px, ${cropState.y}px) scale(${cropState.scale})`;
      }
      updatePhotoPosition();
    };

    const applyPhotoPositionValue = (value) => {
      const text = String(value || '');
      if (photoPosition) photoPosition.value = text;
      const x = text.match(/x:([-\.\d]+)/);
      const y = text.match(/y:([-\.\d]+)/);
      const zoom = text.match(/zoom:([-\.\d]+)/);
      const normalizedX = text.match(/(?:^|[,;]\s*)nx:([-\.\d]+)/);
      const normalizedY = text.match(/(?:^|[,;]\s*)ny:([-\.\d]+)/);
      cropState.x = x ? Number.parseFloat(x[1]) || 0 : cropState.x;
      cropState.y = y ? Number.parseFloat(y[1]) || 0 : cropState.y;
      cropState.scale = zoom ? clamp(Number.parseFloat(zoom[1]) || 1, 1, 2.8) : cropState.scale;
      cropState.normalizedX = normalizedX ? Number.parseFloat(normalizedX[1]) : Number.NaN;
      cropState.normalizedY = normalizedY ? Number.parseFloat(normalizedY[1]) : Number.NaN;
      cropState.normalizedPositionApplied = false;
    };

    const setInputFile = (input, file) => {
      if (!input || !file) return false;
      input.__renaoPendingFile = file;
      if (typeof DataTransfer === 'undefined') return false;
      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        input.__renaoPendingFile = null;
        return true;
      } catch (error) {
        console.warn('Renao heart photo file could not be assigned.', error);
        return false;
      }
    };

    const showProductMedia = () => {
      const mediaWrapper = getProductMediaWrapper();
      if (mediaWrapper) {
        mediaWrapper.classList.remove('has-renao-live-preview');
        mediaWrapper.classList.remove('is-renao-media-returning');
        void mediaWrapper.offsetWidth;
        mediaWrapper.classList.add('is-renao-media-returning');
        window.setTimeout(() => mediaWrapper.classList.remove('is-renao-media-returning'), 900);
      }

      root.classList.remove('has-renao-live-preview');
      if (previewOriginalParent && preview.parentElement !== previewOriginalParent) {
        previewOriginalParent.insertBefore(preview, previewOriginalNext);
      }
      preview.hidden = true;
    };

    const ensureLivePreviewDock = (mediaWrapper) => {
      let dock = mediaWrapper.querySelector('[data-renao-heart-photo-live-preview-dock]');
      if (dock) return dock;

      dock = document.createElement('div');
      dock.className = 'renao-live-preview-dock renao-live-preview-dock--heart-photo';
      dock.dataset.renaoHeartPhotoLivePreviewDock = '';
      dock.innerHTML = `
        <div class="renao-live-preview-dock__bar">
          <span class="renao-live-preview-dock__title">
            <span class="renao-live-preview-dock__mark" aria-hidden="true"></span>
            <span>
              <strong>Live preview</strong>
              <small>Your design before printing</small>
            </span>
          </span>
          <button type="button" data-renao-heart-photo-show-product-media>Product photos</button>
        </div>
        <div class="renao-live-preview-dock__slot" data-renao-heart-photo-live-preview-slot></div>
      `;
      mediaWrapper.insertBefore(dock, mediaWrapper.firstChild);
      dock.querySelector('[data-renao-heart-photo-show-product-media]')?.addEventListener('click', showProductMedia);
      return dock;
    };

    const showLivePreview = () => {
      const mediaWrapper = getProductMediaWrapper();
      if (!mediaWrapper) return;

      const dock = ensureLivePreviewDock(mediaWrapper);
      const slot = dock.querySelector('[data-renao-heart-photo-live-preview-slot]');
      if (!slot) return;

      if (preview.parentElement !== slot) slot.appendChild(preview);
      preview.hidden = false;
      mediaWrapper.classList.remove('is-renao-media-returning');
      mediaWrapper.classList.add('has-renao-live-preview');
      root.classList.add('has-renao-live-preview');
    };

    const activateLivePreview = (event) => {
      if (event?.target?.closest?.('[data-renao-heart-photo-show-product-media]')) return;
      showLivePreview();
    };

    const setPhotoState = () => {
      const isComplete = hasPhoto();
      card.classList.toggle('has-photo', isComplete);
      root.classList.toggle('has-renao-collage-complete', isComplete);
      root.classList.remove('has-photo-error', 'has-renao-collage-error');
      photoInput.setCustomValidity('');
      if (progressCount) progressCount.textContent = `${isComplete ? 1 : 0}/1 photos`;
      if (photoAdjust) photoAdjust.hidden = !isComplete;
    };

    const setExistingPhotoProperty = (url) => {
      let input = form.querySelector('[data-renao-heart-existing-photo-property]');
      if (!url) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[Fotoğraf]';
        input.dataset.renaoHeartExistingPhotoProperty = 'true';
        form.appendChild(input);
      }
      input.value = url;
    };

    root.renaoHasManagedFiles = () => Boolean(
      selectedPhotoFile
      || photoInput.__renaoPendingFile
      || (photoInput.files && photoInput.files.length > 0)
    );

    root.renaoAppendManagedFiles = (formData) => {
      const file = selectedPhotoFile || photoInput.__renaoPendingFile || photoInput.files?.[0] || null;
      if (!formData || !file || !photoInput.name) return false;
      formData.delete(photoInput.name);
      formData.append(photoInput.name, file, file.name || 'foto.jpg');
      return true;
    };

    const getCanonicalNote = () => {
      const value = noteInput.value.trim() || DEFAULT_NOTE;
      return value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .join('\n');
    };

    const syncNote = () => {
      const lines = getCanonicalNote().split('\n');
      notePreview.innerHTML = lines.map((line) => `<span>${escapeHtml(line)}</span>`).join('');
      if (noteCount) noteCount.textContent = `${noteInput.value.length}/${noteInput.maxLength || 90}`;
    };

    const setTone = (tone) => {
      const normalizedTone = tone === 'bw' ? 'bw' : 'color';
      const label = normalizedTone === 'bw' ? 'Siyah beyaz' : 'Renkli';
      root.dataset.renaoHeartTone = normalizedTone;
      preview.dataset.renaoHeartTone = normalizedTone;
      card.dataset.renaoHeartTone = normalizedTone;
      if (toneProperty) toneProperty.value = label;
      toneButtons.forEach((button) => {
        const isSelected = button.dataset.renaoHeartToneOption === normalizedTone;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const setNoteStyle = (style) => {
      const normalizedStyle = style === 'classic' ? 'classic' : 'romantic';
      const label = normalizedStyle === 'classic' ? 'Zarif' : 'Romantik';

      root.dataset.renaoHeartNoteStyle = normalizedStyle;
      preview.dataset.renaoHeartNoteStyle = normalizedStyle;
      card.dataset.renaoHeartNoteStyle = normalizedStyle;
      if (noteStyleProperty) noteStyleProperty.value = label;

      noteStyleButtons.forEach((button) => {
        const isSelected = button.dataset.renaoHeartNoteStyle === normalizedStyle;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const setEditSubmitText = (attempt = 0) => {
      const submitText = form.querySelector('[type="submit"] span');
      if (submitText) {
        submitText.textContent = 'Finish Editing';
        return;
      }
      if (attempt < 12) window.setTimeout(() => setEditSubmitText(attempt + 1), 80);
    };

    const setOrderNoteEditValue = (value, attempt = 0) => {
      if (value === undefined) return;
      const orderNoteInput = form.querySelector('[name="properties[Sipariş Notu]"]');
      if (orderNoteInput) {
        orderNoteInput.value = value;
        return;
      }
      if (attempt < 12) window.setTimeout(() => setOrderNoteEditValue(value, attempt + 1), 80);
    };

    const applyEditState = () => {
      const editKey = new URL(window.location.href).searchParams.get('renao_edit');
      if (!editKey) return;

      let editState = null;
      try {
        editState = JSON.parse(window.sessionStorage.getItem(`renaoCartEdit:${editKey}`) || 'null');
      } catch (error) {
        console.warn('Renao heart photo edit state could not be restored.', error);
      }

      if (!editState) return;

      const properties = normalizeProperties(editState.properties);
      if (editState.key || editKey) {
        form.dataset.renaoEditKey = editState.key || editKey;
        form.dataset.renaoEditStorageKey = `renaoCartEdit:${editState.key || editKey}`;
        setEditSubmitText();
      }

      if (Object.prototype.hasOwnProperty.call(properties, 'Not')) {
        noteInput.value = String(properties.Not || '').slice(0, noteInput.maxLength || 90);
        syncNote();
      }

      if (properties['Yazı Stili']) {
        setNoteStyle(String(properties['Yazı Stili']).toLocaleLowerCase('tr-TR').includes('zarif') ? 'classic' : 'romantic');
      }

      if (properties['Fotoğraf Rengi']) {
        setTone(String(properties['Fotoğraf Rengi']).toLocaleLowerCase('tr-TR').includes('siyah') ? 'bw' : 'color');
      }

      setOrderNoteEditValue(properties['Sipariş Notu']);

      const giftWrapSelect = form.querySelector('[data-renao-gift-wrap-select]');
      if (giftWrapSelect && properties['Hediye Paketi']) giftWrapSelect.value = properties['Hediye Paketi'];

      if (properties['_Fotoğraf'] || properties['Fotoğraf']) {
        existingPhotoUrl = properties['_Fotoğraf'] || properties['Fotoğraf'];
        setExistingPhotoProperty(existingPhotoUrl);
        photoPreview.src = existingPhotoUrl;
        if (photoFileName) photoFileName.textContent = getFileName(existingPhotoUrl);
        if (properties['_Fotoğraf Konumu']) applyPhotoPositionValue(properties['_Fotoğraf Konumu']);
        setPhotoState();
        applyPhotoTransform();
      }
    };

    const setSelectedFile = (file) => {
      if (!file) return;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      selectedPhotoFile = file;
      objectUrl = URL.createObjectURL(file);
      existingPhotoUrl = '';
      setExistingPhotoProperty('');
      setInputFile(photoInput, file);
      cropState = { scale: 1, x: 0, y: 0 };
      photoPreview.src = objectUrl;
      if (photoFileName) photoFileName.textContent = file.name;
      setPhotoState();
      showLivePreview();
      applyPhotoTransform();
      window.requestAnimationFrame(openCrop);
    };

    photoInput.addEventListener('change', () => {
      const file = photoInput.files && photoInput.files[0];
      if (file) {
        setSelectedFile(file);
      } else if (existingPhotoUrl) {
        photoPreview.src = existingPhotoUrl;
        if (photoFileName) photoFileName.textContent = getFileName(existingPhotoUrl);
      } else {
        selectedPhotoFile = null;
        photoInput.__renaoPendingFile = null;
        photoPreview.removeAttribute('src');
        if (photoFileName) photoFileName.textContent = 'Not Selected';
      }
      setPhotoState();
      showLivePreview();
    });

    bulkButton?.addEventListener('click', () => {
      showLivePreview();
      bulkInput?.click();
    });
    bulkInput?.addEventListener('change', () => {
      const file = bulkInput.files && bulkInput.files[0];
      if (file) setSelectedFile(file);
    });
    photoButton?.addEventListener('click', () => {
      showLivePreview();
      photoInput.click();
    });
    const openCrop = () => {
      const imageUrl = getPhotoUrl();
      if (!imageUrl || !cropModal || !cropImage) return;
      showLivePreview();
      cropImage.src = imageUrl;
      if (cropZoom) cropZoom.value = String(cropState.scale);
      cropModal.hidden = false;
      document.documentElement.classList.add('renao-heart-crop-open');
      cropState.normalizedPositionApplied = false;
      window.requestAnimationFrame(applyPhotoTransform);
    };

    const closeCrop = () => {
      if (!cropModal) return;
      cropModal.hidden = true;
      document.documentElement.classList.remove('renao-heart-crop-open');
      cropState.normalizedPositionApplied = false;
      applyPhotoTransform();
      dragState = null;
    };

    const moveCrop = (direction) => {
      const amount = 14;
      if (direction === 'left') cropState.x -= amount;
      if (direction === 'right') cropState.x += amount;
      if (direction === 'up') cropState.y -= amount;
      if (direction === 'down') cropState.y += amount;
      applyPhotoTransform();
    };

    const resetCrop = () => {
      cropState = { scale: 1, x: 0, y: 0 };
      if (cropZoom) cropZoom.value = '1';
      applyPhotoTransform();
    };

    photoAdjust?.addEventListener('click', openCrop);
    toneButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setTone(button.dataset.renaoHeartToneOption);
        showLivePreview();
      });
    });
    noteInput.addEventListener('input', () => {
      syncNote();
      showLivePreview();
    });
    noteInput.addEventListener('focus', showLivePreview);
    noteStyleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setNoteStyle(button.dataset.renaoHeartNoteStyle);
        showLivePreview();
      });
    });

    const openProof = () => {
      if (!proofModal || !proofStage) return;
      showLivePreview();
      proofStage.innerHTML = '';
      const clone = preview.cloneNode(true);
      clone.hidden = false;
      proofStage.appendChild(clone);
      proofModal.hidden = false;
      document.documentElement.classList.add('renao-proof-modal-open');

      // A proof card is wider than the live product preview on many desktop and
      // mobile viewports. Rebuild its pixel translation from the normalized crop
      // recipe instead of copying the live preview's fixed pixel transform.
      window.requestAnimationFrame(() => {
        const clonePhoto = clone.querySelector('[data-renao-heart-photo-preview]');
        const cloneFrame = clonePhoto?.parentElement;
        const cloneWidth = cloneFrame?.clientWidth || cloneFrame?.getBoundingClientRect?.().width || 0;
        const cloneHeight = cloneFrame?.clientHeight || cloneFrame?.getBoundingClientRect?.().height || 0;
        if (
          clonePhoto &&
          cloneWidth &&
          cloneHeight &&
          Number.isFinite(cropState.normalizedX) &&
          Number.isFinite(cropState.normalizedY)
        ) {
          clonePhoto.style.transform = `translate(${cropState.normalizedX * cloneWidth}px, ${
            cropState.normalizedY * cloneHeight
          }px) scale(${cropState.scale})`;
        }
      });
    };

    const closeProof = () => {
      if (!proofModal) return;
      proofModal.hidden = true;
      document.documentElement.classList.remove('renao-proof-modal-open');
    };

    proofOpen?.addEventListener('click', openProof);
    proofCloseButtons.forEach((button) => button.addEventListener('click', closeProof));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeProof();
        closeCrop();
      }
    });

    cropCancelButtons.forEach((button) => button.addEventListener('click', closeCrop));
    cropApply?.addEventListener('click', closeCrop);
    cropZoom?.addEventListener('input', () => {
      cropState.scale = clamp(Number(cropZoom.value) || 1, 1, 2.8);
      applyPhotoTransform();
    });
    cropZoomStepButtons.forEach((button) => {
      button.addEventListener('click', () => {
        cropState.scale = clamp(cropState.scale + Number(button.dataset.renaoHeartCropZoomStep || 0), 1, 2.8);
        if (cropZoom) cropZoom.value = String(cropState.scale);
        applyPhotoTransform();
      });
    });
    cropMoveButtons.forEach((button) => {
      button.addEventListener('click', () => moveCrop(button.dataset.renaoHeartCropMove));
    });
    cropReset?.addEventListener('click', resetCrop);
    cropStage?.addEventListener('pointerdown', (event) => {
      if (!hasPhoto()) return;
      dragState = { x: event.clientX, y: event.clientY, baseX: cropState.x, baseY: cropState.y };
      cropStage.setPointerCapture(event.pointerId);
    });
    cropStage?.addEventListener('pointermove', (event) => {
      if (!dragState) return;
      cropState.x = dragState.baseX + (event.clientX - dragState.x);
      cropState.y = dragState.baseY + (event.clientY - dragState.y);
      applyPhotoTransform();
    });
    const finishCropDrag = (event) => {
      if (!dragState) return;
      dragState = null;
      if (cropStage?.hasPointerCapture(event.pointerId)) cropStage.releasePointerCapture(event.pointerId);
    };
    cropStage?.addEventListener('pointerup', finishCropDrag);
    cropStage?.addEventListener('pointercancel', finishCropDrag);

    form.addEventListener(
      'submit',
      (event) => {
        // Persist exactly the same canonical note that the customer sees in the
        // live preview, proof modal and generated PDF.
        noteInput.value = getCanonicalNote();
        syncNote();
        if (hasPhoto()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        root.classList.add('has-photo-error');
        root.classList.add('has-renao-collage-error');
        photoInput.setCustomValidity('Choose a photo before adding to cart.');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      true
    );

    syncNote();
    setNoteStyle(root.dataset.renaoHeartNoteStyle || 'romantic');
    setTone(root.dataset.renaoHeartTone || 'color');
    applyEditState();
    setPhotoState();
    applyPhotoTransform();
    preview.hidden = true;
    root.addEventListener('focusin', activateLivePreview);
    root.addEventListener('click', activateLivePreview);
  };

  const boot = () => {
    document.querySelectorAll('[data-renao-heart-photo-personalizer]').forEach(initHeartPhotoPersonalizer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
